import { Icon } from "@/assets/icons";
import { cn } from "@/utils";
import { ja } from "date-fns/locale/ja";
import { forwardRef, ReactNode, useRef } from "react";
import ReactDatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./index.css";
registerLocale("ja", ja);

type DateFormat = string | string[] | undefined;

type DateTypes = {
  label?: string;
  value?: Date | null | undefined;
  className?: string;
  containerClassName?: string;
  dateFormat?: DateFormat;
  placeholder?: string;
  selected?: Date | null;
  onChange?: (date: Date | string | null) => void;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  customInput?: any;
  showTimeSelect?: boolean;
  error?: string;
  minDate?: Date;
  maxDate?: Date;
  showYearPicker?: boolean;
  icon?: ReactNode;
  hideIcon?: boolean;
  showYearDropdown?: boolean;
  showMonthYearPicker?: boolean;
  yearDropdownItemNumber?: number;
  popperPlacement?:
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "top-start"
    | "top-end"
    | "bottom-start"
    | "bottom-end"
    | "left-start"
    | "left-end"
    | "right-start"
    | "right-end";
};

export const DatePicker = forwardRef<HTMLInputElement, DateTypes>(
  ({
    label,
    value,
    className,
    containerClassName,
    dateFormat = "yyyy/MM/dd",
    placeholder = "YYYY/MM/DD",
    selected,
    onChange,
    disabled,
    readOnly,
    required,
    error,
    showTimeSelect,
    icon,
    hideIcon,
    showYearPicker,
    showYearDropdown,
    showMonthYearPicker,
    yearDropdownItemNumber,
    popperPlacement,
    ...props
  }) => {
    const ref = useRef<ReactDatePicker>(null);

    const handleDateChange = (date: Date | null) => {
      onChange?.(date?.toISOString() || null);
    };

    return (
      <div className={`relative ${containerClassName}`}>
        {label && (
          <label
            className={cn(
              `absolute left-4 top-0 -translate-y-1/2 px-1 text-xs z-[2] pointer-events-none`,
              error ? "text-error_main" : "text-grey-600"
            )}
            style={{
              background: disabled
                ? "linear-gradient(to bottom, transparent 50%, #F4F6F8 50%)"
                : "#ffffff",
            }}
          >
            {label}
            {required && <span className="text-error_main ml-1">*</span>}
          </label>
        )}
        <div className="relative date_picker-container">
          <ReactDatePicker
            ref={ref}
            dateFormatCalendar='yyyy年MM月'
            selected={value ? new Date(value) : selected}
            onChange={handleDateChange}
            dateFormat={dateFormat}
            placeholderText={placeholder}
            disabled={disabled}
            required={required}
            locale={"ja"}
            wrapperClassName={"w-full"}
            calendarClassName="bg-white rounded-lg shadow-md"
            maxDate={new Date("01/01/10000")}
            popperClassName="popper-class"
            popperPlacement={popperPlacement}
            dayClassName={(date) =>
              date.getDay() === 0
                ? "!text-error_main"
                : date.getDay() === 6
                  ? "!text-cyan"
                  : ""
            }
            weekDayClassName={(date) =>
              date.getDay() === 0
                ? "!text-error_main"
                : date.getDay() === 6
                  ? "!text-cyan"
                  : ""
            }
            className={cn(
              `h-11 px-4 flex items-center justify-center w-full text-base`,
              className || "",
              error
                ? "!border-error_main hover:!border-error_main focus:!border-error_main" // đồng bộ trạng thái lỗi với input khác
                : "" /* tránh undefined trong className*/,
              disabled && "bg-grey-200 cursor-not-allowed" // đồng bộ trạng thái disabled với input khác
            )}
            showTimeSelect={showTimeSelect}
            timeCaption="時間"
            showYearPicker={showYearPicker}
            showYearDropdown={showYearDropdown}
            showMonthYearPicker={showMonthYearPicker}
            yearDropdownItemNumber={yearDropdownItemNumber}
            {...props}
          />
          {!hideIcon && (
            <button
              type="button"
              className="absolute top-1/2 -translate-y-1/2 right-4 cursor-pointer"
              onClick={() => {
                ref.current?.setOpen(true);
              }}
            >
              {icon || <Icon name="calendar" />}
            </button>
          )}
        </div>
        {error && <p className="text-error_main text-xs mt-1">{error}</p>}
      </div>
    );
  }
);
